import {
  ORDER_MODIFICATION_TARGET_ITEM,
  ORDER_MODIFICATION_TARGET_ITEM_PRODUCT,
  ORDER_MODIFICATION_TARGET_ITEM_SECTION,
  ORDER_MODIFICATION_TARGET_ITEM_SECTION_CATEGORY,
  PRICE_TYPE_AVERAGE,
  PRICE_TYPE_CHEAPER,
  PRICE_TYPE_MORE_EXPENSIVE,
  ROUND_PRICE_DOWN,
  ROUND_PRICE_UP,
} from '@/constants'
import { fixFloat, getValue, hashObject, naturalJoin, clone } from '@/helpers'
import { normalizeOrderItemProducts } from '@/utils/Company/Order/Item/Product'
import { normalizeOrderItemExtras } from '@/utils/Extra'
import { getTotalOrderItemModifications } from '@/utils/Company/Order'

/**
 * Function to calculate the order item value based on the provided order item and value.
 *
 * @param {object} orderItem - The order item containing portion, size, products, and numberUniqueProducts.
 * @param {number|string} value - The value to calculate.
 * @return {number} The calculated value for the order item.
 */
export function getOrderItemValue({ orderItem, value }) {
  const { portion, size, products, numberUniqueProducts } = orderItem
  const numberProducts = products.length
  const companySectionPortionId = portion?.id
  const companySectionSizeId = size?.id
  const dictionary = {
    companySectionPortionId,
    companySectionSizeId,
    numberUniqueProducts,
    numberProducts,
  }

  return getValue(value, { dictionary })
}

/**
 * Get modifications for the given order item.
 *
 * @param {Object} orderItem - The order item
 * @param {Array} modifications - The modifications array (default is an empty array)
 * @return {Array} The filtered modifications array
 */
export function getOrderItemModifications({ orderItem, modifications = [] }) {
  return modifications.filter(({ target, targetId }) => {
    switch (target) {
      case ORDER_MODIFICATION_TARGET_ITEM:
        return targetId === orderItem.id
        break

      case ORDER_MODIFICATION_TARGET_ITEM_PRODUCT:
        return orderItem.products.some(({ id }) => targetId === id)
        break

      case ORDER_MODIFICATION_TARGET_ITEM_SECTION:
        return targetId === orderItem.section.id
        break

      case ORDER_MODIFICATION_TARGET_ITEM_SECTION_CATEGORY:
        return orderItem.products.some(
          ({ category }) => targetId === category?.id,
        )
        break
    }

    return false
  })
}

/**
 * Normalize order items by processing each item in the order.
 *
 * @param {Object} order - The order object containing items to be normalized.
 * @return {Promise<Array>} A promise that resolves to an array of normalized order items.
 */
export function normalizeOrderItems(order) {
  return order.items.map(orderItem => normalizeOrderItem({ orderItem, order }))
}

/**
 * Normalizes an order item by modifying and adding properties such as quantity, products, modifications, and prices.
 *
 * @param {object} orderItem - The order item to be normalized
 * @param {object} order - The order object containing information about modifications and other properties
 * @return {object} The normalized order item with updated properties
 */
export function normalizeOrderItem({ orderItem, order }) {
  orderItem = clone(orderItem)

  orderItem = {
    quantity: 1,
    numberProducts: 1,
    observations: undefined,
    section: undefined,
    portion: undefined,
    size: undefined,
    ...orderItem,
    products: normalizeOrderItemProducts(orderItem),
    get isCompositionProducts() {
      return this.products?.length > 1
    },
    get totalPrice() {
      const { unitPrice, quantity } = this

      let totalPrice = unitPrice * quantity

      totalPrice = fixFloat(totalPrice)

      return totalPrice
    },
    get numberUniqueProducts() {
      return [...new Set(this.products.map(({ id }) => id))].length
    },
    get modifications() {
      const orderItem = this
      const { modifications } = order

      return getOrderItemModifications({
        orderItem,
        modifications,
      })
    },
    get modificationsValue() {
      const orderItem = this
      const { modifications, quantity } = orderItem

      let modificationsValue = getTotalOrderItemModifications({
        orderItem,
        modifications,
      })

      modificationsValue /= quantity
      modificationsValue = fixFloat(modificationsValue)

      return modificationsValue
    },
    get totalModificationsValue() {
      const { modificationsValue, quantity } = this

      let totalModificationsValue = modificationsValue * quantity

      totalModificationsValue = fixFloat(totalModificationsValue)

      return totalModificationsValue
    },
    get totalOriginalPrice() {
      const { originalPrice, quantity } = this

      let totalOriginalPrice = originalPrice * quantity

      totalOriginalPrice = fixFloat(totalOriginalPrice)

      return totalOriginalPrice
    },
    get sizePortion() {
      const { size, portion } = this

      let sizePortion

      if (portion && size) {
        const sizePortions = size.portions ?? size.allowedPortions ?? []

        sizePortion = sizePortions.find(({ id }) => id === portion?.id)
      }

      return sizePortion
    },
    get productsRange() {
      const item = this

      return app.getItemProductsRange(item)
    },
    get totalBasePrice() {
      const { basePrice, quantity } = this

      let totalBasePrice = basePrice * quantity

      totalBasePrice = fixFloat(totalBasePrice)

      return totalBasePrice
    },
    get productsPrice() {
      const { section, sizePortion, products } = this
      const priceMode =
        sizePortion?.priceMode ?? sizePortion?.priceType ?? PRICE_TYPE_AVERAGE

      let totalProductsPrice = products.map(({ price }) =>
        getOrderItemValue({ orderItem: orderItem, value: price }),
      )

      switch (priceMode) {
        case PRICE_TYPE_MORE_EXPENSIVE:
          totalProductsPrice = Math.max(...totalProductsPrice)
          break

        case PRICE_TYPE_CHEAPER:
          totalProductsPrice = Math.min(...totalProductsPrice)
          break

        default:
          totalProductsPrice =
            totalProductsPrice.reduce((acc, price) => acc + price, 0) /
            totalProductsPrice.length
          break
      }

      const roundPriceMode = section?.roundPriceMode ?? section?.roundPrice

      switch (roundPriceMode) {
        case ROUND_PRICE_UP:
          totalProductsPrice = Math.ceil(totalProductsPrice)
          break

        case ROUND_PRICE_DOWN:
          totalProductsPrice = Math.floor(totalProductsPrice)
          break
      }

      totalProductsPrice = fixFloat(totalProductsPrice)

      return totalProductsPrice
    },
    get totalProductsPrice() {
      const { quantity, productsPrice } = this

      let totalProductsPrice = productsPrice * quantity

      totalProductsPrice = fixFloat(totalProductsPrice)

      return totalProductsPrice
    },
    get selectedExtras() {
      const { extras } = this

      return extras.filter(({ selectedItems }) => selectedItems.length > 0)
    },
    get selectedProductsExtras() {
      const { products = [] } = this

      return products.flatMap(({ selectedExtras }) => selectedExtras) ?? []
    },
    get productsExtras() {
      const { products } = this

      return products.flatMap(({ extras }) => extras)
    },
    get extrasPrice() {
      const { selectedExtras, selectedProductsExtras } = this

      let extras = [...selectedExtras, ...selectedProductsExtras]
      let extrasPrice = extras.reduce(
        (extraAcc, { totalPrice }) => extraAcc + totalPrice,
        0,
      )

      extrasPrice = fixFloat(extrasPrice)

      return extrasPrice
    },
    get totalExtrasPrice() {
      const { quantity, extrasPrice } = this

      let totalExtrasPrice = extrasPrice * quantity

      totalExtrasPrice = fixFloat(totalExtrasPrice)

      return totalExtrasPrice
    },
    get basePrice() {
      return this.productsPrice
    },
    get sectionCategoriesTitle() {
      const { section, products } = this

      let title = []
      let categoriesName = Array.from(
        products.reduce(
          (set, { category }) =>
            category ? set.add(category.name ?? category.title) : set,
          new Set(),
        ),
      )

      title.push(section.name)

      if (categoriesName.length > 0) {
        categoriesName = naturalJoin(categoriesName)

        title.push(categoriesName)
      }

      title = title.join(' > ')

      return title
    },
    get hash() {
      const {
        section,
        portion,
        size,
        products,
        selectedExtras,
        numberProducts,
        observations,
      } = this
      const productsData = products
        .map(({ id, removedIngredients = [], selectedExtras }) => {
          const removedIngredientsId = removedIngredients
            .map(({ id }) => id)
            .sort()
          const product = { id, removedIngredientsId, selectedExtras }

          return product
        })
        .sort()
      const extrasItemId = selectedExtras
        .flatMap(({ items }) =>
          items.flatMap(({ id, quantity }) => `${id}-${quantity}`),
        )
        .sort()
      const dataToHash = {
        sectionId: section?.id,
        portionId: portion?.id,
        sizeId: size?.id,
        numberProducts,
        products: productsData,
        extrasItemId,
        observations,
      }

      return hashObject(dataToHash)
    },
  }

  let extras = orderItem.extras ?? orderItem.section?.extras ?? []

  extras = normalizeOrderItemExtras({ orderItem: orderItem, extras })

  orderItem.extras = extras

  if (!('title' in orderItem))
    Object.defineProperty(orderItem, 'title', {
      get: function () {
        const { isCompositionProducts } = this

        let title = []

        if (isCompositionProducts) {
          const { sectionCategoriesTitle } = this

          title.push(sectionCategoriesTitle)
        } else {
          const { products } = this
          const [firstProduct] = products

          if (firstProduct) title.push(firstProduct.title ?? firstProduct.name)
        }

        title = title.join(' > ')

        return title
      },
    })

  if (!('description' in orderItem))
    Object.defineProperty(orderItem, 'description', {
      get: function () {
        const { isCompositionProducts } = this

        let description

        if (!isCompositionProducts) {
          const { sectionCategoriesTitle } = this

          description = sectionCategoriesTitle
        }

        return description
      },
    })

  if (!('unitPrice' in orderItem))
    Object.defineProperty(orderItem, 'unitPrice', {
      get: function () {
        const { originalPrice, modificationsValue } = this

        let unitPrice = originalPrice + modificationsValue

        unitPrice = fixFloat(unitPrice)

        return unitPrice
      },
    })

  if (!('originalPrice' in orderItem))
    Object.defineProperty(orderItem, 'originalPrice', {
      get: function () {
        const { productsPrice, extrasPrice } = this

        let originalPrice = productsPrice + extrasPrice

        originalPrice = fixFloat(originalPrice)

        return originalPrice
      },
    })

  return orderItem
}
